// Fondations partagées par les renderers d'export (FanChart, AscendanceChart,
// SablierChart) : presets de style, géométrie polaire, et la boîte-personne SVG.
// Tout est en SVG natif (pas de foreignObject) pour permettre un export PDF
// vectoriel via svg2pdf.js. La géométrie reprend celle validée sur la maquette.

import React from 'react';
import { Membre } from '../../types';
import { GEN_COLORS, yearsRange } from '../../utils/treeVisuals';

// Palette pastel (preset Décoratif) — versions douces de GEN_COLORS
export const PASTELS = ['#D9CBF0', '#BBD6F2', '#BFE9D2', '#F7E2B0', '#F6C3BE', '#F8CFE0', '#C7CDF0'];
// Palette ardoise (preset sombre élégant) — teintes profondes et sourdes
export const SLATES = ['#3B4A63', '#2F5D63', '#4A5A44', '#63455A', '#3D4E6B', '#5A4A3A', '#41556B'];

export type StyleKey = 'moderne' | 'classique' | 'decoratif' | 'ardoise' | 'vitrail';

export interface StylePreset {
  key: StyleKey;
  name: string;
  desc: string;
  font: string;
  bg?: string;            // fond forcé ; sinon suit le thème d'export
  forceLight?: boolean;   // reste clair même en export sombre (rendu « impression »)
  sectorFill: (gen: number) => string;
  sectorText: string;
  sectorStroke: string;
  sectorStrokeW: number;
  boxFill: (gen: number) => string;
  boxText: string;
  boxStroke: string;      // 'none' possible
  boxStrokeW: number;
  boxRadius: number;
  edge: string;
  edgeW: number;
  weight: number;
  weightSub: number;
  italicSub?: boolean;
}

export const STYLE_PRESETS: Record<StyleKey, StylePreset> = {
  moderne: {
    key: 'moderne',
    name: 'Moderne',
    desc: 'Couleurs vives par génération, coins arrondis, sans-serif.',
    font: 'ui-sans-serif, system-ui, "Segoe UI", Roboto, sans-serif',
    sectorFill: g => GEN_COLORS[g % GEN_COLORS.length],
    sectorText: '#FFFFFF',
    sectorStroke: 'rgba(255,255,255,.55)', sectorStrokeW: 1.5,
    boxFill: g => GEN_COLORS[g % GEN_COLORS.length],
    boxText: '#FFFFFF', boxStroke: 'none', boxStrokeW: 0, boxRadius: 9,
    edge: '#9CB3A6', edgeW: 1.6, weight: 700, weightSub: 500,
  },
  classique: {
    key: 'classique',
    name: 'Classique',
    desc: 'Noir & blanc, filets fins, sérif. Façon registre imprimé.',
    font: 'Georgia, "Times New Roman", serif',
    bg: '#FBFAF5', forceLight: true,
    sectorFill: () => '#FBFAF5',
    sectorText: '#1C1B17',
    sectorStroke: '#8A8578', sectorStrokeW: 0.8,
    boxFill: () => '#FFFFFF',
    boxText: '#1C1B17', boxStroke: '#2B2A24', boxStrokeW: 1, boxRadius: 0,
    edge: '#2B2A24', edgeW: 1, weight: 700, weightSub: 400, italicSub: true,
  },
  decoratif: {
    key: 'decoratif',
    name: 'Décoratif',
    desc: 'Pastel, formes rondes, traits épais. Adapté aux enfants.',
    font: '"Trebuchet MS", "Segoe UI Rounded", ui-sans-serif, sans-serif',
    bg: '#FFF9FC',
    sectorFill: g => PASTELS[g % PASTELS.length],
    sectorText: '#3A2E3D',
    sectorStroke: '#FFFFFF', sectorStrokeW: 3,
    boxFill: g => PASTELS[g % PASTELS.length],
    boxText: '#3A2E3D', boxStroke: '#FFFFFF', boxStrokeW: 3, boxRadius: 16,
    edge: '#D9B8CC', edgeW: 3, weight: 700, weightSub: 600,
  },
  ardoise: {
    key: 'ardoise',
    name: 'Ardoise',
    desc: 'Sombre et élégant : teintes profondes, texte clair. Écran & présentation.',
    font: 'ui-sans-serif, system-ui, "Segoe UI", Roboto, sans-serif',
    bg: '#0F1620',
    sectorFill: g => SLATES[g % SLATES.length],
    sectorText: '#E7EEF5',
    sectorStroke: 'rgba(255,255,255,.16)', sectorStrokeW: 1.2,
    boxFill: g => SLATES[g % SLATES.length],
    boxText: '#E7EEF5', boxStroke: 'rgba(255,255,255,.14)', boxStrokeW: 1, boxRadius: 8,
    edge: '#4A5A6B', edgeW: 1.4, weight: 700, weightSub: 500,
  },
  vitrail: {
    key: 'vitrail',
    name: 'Vitrail',
    desc: 'Couleurs saturées cernées de noir, façon vitrail. Fort impact visuel.',
    font: '"Trebuchet MS", ui-sans-serif, system-ui, sans-serif',
    bg: '#FCFBF7',
    sectorFill: g => GEN_COLORS[g % GEN_COLORS.length],
    sectorText: '#FFFFFF',
    sectorStroke: '#161616', sectorStrokeW: 2.5,
    boxFill: g => GEN_COLORS[g % GEN_COLORS.length],
    boxText: '#FFFFFF', boxStroke: '#161616', boxStrokeW: 2, boxRadius: 4,
    edge: '#161616', edgeW: 2, weight: 800, weightSub: 600,
  },
};

export const STYLE_ORDER: StyleKey[] = ['moderne', 'classique', 'decoratif', 'ardoise', 'vitrail'];

// Couleur d'accent (mise en avant de la personne racine) par preset
export function accentStroke(style: StylePreset): string {
  if (style.key === 'classique') return '#B7791F';
  if (style.key === 'decoratif') return '#E58AB0';
  return '#F59E0B';
}

// Fond du canevas : forcé par le preset, sinon selon le thème d'export
export function resolveBg(style: StylePreset, isDark: boolean): string {
  return style.bg ?? (isDark ? '#0F1712' : '#F9FCF7');
}

export const fullName = (m: Membre): string => `${m.prenom} ${m.nom}`.trim();
export const memberYears = (m: Membre): string => yearsRange(m.date_naissance, m.date_deces);

// ─── Géométrie polaire ───────────────────────────────────────────────────────
const rad = (a: number) => (a * Math.PI) / 180;
export const polar = (cx: number, cy: number, r: number, a: number): [number, number] =>
  [cx + r * Math.cos(rad(a)), cy + r * Math.sin(rad(a))];

export function sectorPath(cx: number, cy: number, ri: number, ro: number, a0: number, a1: number): string {
  const large = a1 - a0 > 180 ? 1 : 0;
  const [ox0, oy0] = polar(cx, cy, ro, a0);
  const [ox1, oy1] = polar(cx, cy, ro, a1);
  const [ix1, iy1] = polar(cx, cy, ri, a1);
  const [ix0, iy0] = polar(cx, cy, ri, a0);
  if (ri <= 0.5) {
    return `M ${cx} ${cy} L ${ox0} ${oy0} A ${ro} ${ro} 0 ${large} 1 ${ox1} ${oy1} Z`;
  }
  return `M ${ox0} ${oy0} A ${ro} ${ro} 0 ${large} 1 ${ox1} ${oy1} `
    + `L ${ix1} ${iy1} A ${ri} ${ri} 0 ${large} 0 ${ix0} ${iy0} Z`;
}

// ─── Boîte-personne (rectangle SVG + barre de sexe + 2 lignes) ─────────────────
interface PersonBoxProps {
  x: number; y: number; w: number; h: number;
  membre: Membre; gen: number; style: StylePreset;
  emphasize?: boolean;    // met en avant la personne racine
  neutral?: boolean;      // teinte neutre (ex. descendants du sablier)
}

export const PersonBox: React.FC<PersonBoxProps> = ({ x, y, w, h, membre, gen, style, emphasize, neutral }) => {
  const deceased = !!membre.date_deces;
  const fill = neutral
    ? (style.forceLight ? '#F1EFE8' : 'rgba(150,150,150,.18)')
    : style.boxFill(gen);
  const genderColor = membre.sexe === 'Homme' ? '#3B82F6' : membre.sexe === 'Femme' ? '#EC4899' : '#6B7280';
  const showBar = !style.forceLight || neutral;

  const strokeColor = emphasize
    ? accentStroke(style)
    : (style.boxStroke === 'none' ? undefined : style.boxStroke);
  const strokeWidth = emphasize
    ? (style.boxStroke === 'none' ? 3 : style.boxStrokeW + 1.5)
    : (style.boxStroke === 'none' ? 0 : style.boxStrokeW);

  return (
    <g>
      <rect
        x={x} y={y} width={w} height={h} rx={style.boxRadius} ry={style.boxRadius}
        fill={fill}
        stroke={strokeColor} strokeWidth={strokeWidth || undefined}
      />
      {showBar && (
        <rect x={x} y={y} width={4} height={h} fill={deceased ? '#9CA3AF' : genderColor} opacity={0.9} />
      )}
      <text
        x={x + w / 2} y={y + 19} fill={style.boxText} fontFamily={style.font}
        fontSize={12.5} fontWeight={style.weight} textAnchor="middle"
      >
        {(deceased ? '† ' : '') + fullName(membre)}
      </text>
      <text
        x={x + w / 2} y={y + 34} fill={style.boxText} fontFamily={style.font}
        fontSize={10} fontWeight={style.weightSub} textAnchor="middle" opacity={0.82}
        fontStyle={style.italicSub ? 'italic' : undefined}
      >
        {memberYears(membre)}
      </text>
    </g>
  );
};
